import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API } from "../lib/constants";
import "../components/landing/spotfie-landing.css";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronLeft,
  Download,
  LockKeyhole,
  LogIn,
  Menu,
  Minus,
  Plus,
  X
} from "lucide-react";
const images = {
  hero: "/images/spotfie-hero.jpg",
  selfie: "/images/spotfie-selfie.jpg",
  friends: "/images/spotfie-friends.jpg",
  candid: "/images/spotfie-candid.jpg"
};
const navLinks = [
  { label: "The journey", href: "#journey" },
  { label: "For teams", href: "#for-teams" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" }
];
const faqItems = [
  {
    question: "Do guests need an app or an account?",
    answer: "Neither. They scan the event QR, open a page in their browser, take a selfie, and see their private album. It is designed to feel easy even in the middle of an event."
  },
  {
    question: "What happens to a guest's selfie?",
    answer: "The selfie is used to find that guest in the event photos, then the original selfie is discarded. It is a way into their album, not a photo added to the gallery."
  },
  {
    question: "Can our photographers upload throughout the event?",
    answer: "Yes. Your photographers can add photos to the same event folder as the shoot unfolds. New photos can then be processed into the right guest albums."
  },
  {
    question: "Can the guest page carry our branding?",
    answer: "Yes. Make the guest experience feel like your event. The Pro Event Pass also removes the Spotfie footer for a fully white-label handoff."
  },
  {
    question: "Is there a monthly subscription?",
    answer: "No. Your first event is free. The Pro Event Pass is a one-time price per event when you want the white-label extras."
  }
];
let activeDialogCount = 0;
let pageOverflowBeforeDialogs = "";
let focusBeforeDialogs = null;
function Logo({ light = false }) {
  return <a className={`brand-logo ${light ? "brand-logo-light" : ""}`} href="#top" aria-label="Spotfie, back to top">
      <span className="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 38 38" fill="none">
          <path d="M5 13V8a3 3 0 0 1 3-3h5M25 5h5a3 3 0 0 1 3 3v5M33 25v5a3 3 0 0 1-3 3h-5M13 33H8a3 3 0 0 1-3-3v-5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
          <circle cx="14" cy="17" r="1.8" fill="currentColor" />
          <circle cx="24" cy="17" r="1.8" fill="currentColor" />
          <path d="M14.5 23.5c2.6 3 6.4 3 9 0" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" />
        </svg>
      </span>
      <span className="brand-word">spotfie<span>.</span></span>
    </a>;
}
function Reveal({ children, className = "", delay = 0 }) {
  const reduceMotion = useReducedMotion();
  return <motion.div
    className={className}
    initial={reduceMotion ? false : { opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.16 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
      {children}
    </motion.div>;
}
function SectionLabel({ number, children }) {
  return <p className="section-label"><span>{number}</span><span className="label-line" />{children}</p>;
}
function QRPattern({ x, y, size = 8 }) {
  const filled = (row, col) => {
    const finder = (originRow, originCol) => {
      const r = row - originRow;
      const c = col - originCol;
      if (r < 0 || r > 4 || c < 0 || c > 4) return null;
      return r === 0 || r === 4 || c === 0 || c === 4 || r === 2 && c === 2;
    };
    const corner = finder(0, 0) ?? finder(0, 10) ?? finder(10, 0);
    return corner ?? (row * 7 + col * 11 + row * col) % 7 < 3;
  };
  return <g aria-hidden="true">
      {Array.from(
    { length: 15 },
    (_, row) => Array.from(
      { length: 15 },
      (_2, col) => filled(row, col) ? <rect key={`${row}-${col}`} x={x + col * size} y={y + row * size} width={size - 1.3} height={size - 1.3} rx="0.7" fill="#272039" /> : null
    )
  )}
    </g>;
}
function EntranceIllustration() {
  const reduceMotion = useReducedMotion();
  return <motion.div
    className="entrance-art"
    animate={reduceMotion ? void 0 : { y: [0, -10, 0] }}
    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
  >
      <svg viewBox="0 0 720 620" role="img" aria-label="Dimensional illustration of an event doorway with a QR sign for guests">
        <defs>
          <linearGradient id="archInside" x1="0" x2="1" y1="0" y2="1">
            <stop stopColor="#c6b9f1" />
            <stop offset="1" stopColor="#9c8ddb" />
          </linearGradient>
          <linearGradient id="signFace" x1="0" x2="1" y1="0" y2="1">
            <stop stopColor="#fffefb" />
            <stop offset="1" stopColor="#f2efff" />
          </linearGradient>
        </defs>
        <ellipse cx="360" cy="546" rx="310" ry="45" fill="#b4a3db" opacity=".28" />
        <path d="M76 548h570M122 574h495M198 599h315" stroke="#8e82b8" strokeWidth="1.5" opacity=".38" />
        <path d="M170 511V250C170 132 251 65 356 65c106 0 187 67 187 185v261H170Z" fill="#aa97df" stroke="#302849" strokeWidth="3" />
        <path d="M543 252c0-117-81-185-187-185M543 252v259M170 511h373" fill="none" stroke="#302849" strokeWidth="3" />
        <path d="M145 511V249C145 132 227 80 332 80c106 0 186 52 186 169v262H145Z" fill="#e2daf9" stroke="#302849" strokeWidth="3" />
        <path d="M204 511V253c0-80 54-124 128-124s128 44 128 124v258H204Z" fill="url(#archInside)" stroke="#302849" strokeWidth="3" />
        <path d="M217 511V255c0-73 48-113 115-113s115 40 115 113v256M232 511V258c0-64 42-101 100-101s100 37 100 101v253" fill="none" stroke="#5d4d9d" strokeWidth="2" opacity=".52" />
        <path d="M145 511h373l25 0 61 48H89l56-48Z" fill="#c7baf0" stroke="#302849" strokeWidth="3" />
        <path d="M145 527h392M122 545h462" stroke="#5b4d93" strokeWidth="1.5" opacity=".5" />
        <circle cx="290" cy="321" r="26" fill="#e8bf9e" />
        <path d="M259 318c-5-31 13-49 34-49 24 0 36 22 27 51-9-19-27-22-61-2Z" fill="#312747" />
        <path d="M230 509v-68c0-50 24-78 60-78 39 0 64 28 64 78v68H230Z" fill="#f0d27c" stroke="#302849" strokeWidth="2.5" />
        <path d="M291 363v145" stroke="#a0874b" strokeWidth="2" opacity=".6" />
        <circle cx="382" cy="335" r="24" fill="#b97969" />
        <path d="M358 336c-5-28 7-42 29-42 18 0 29 17 22 42-10-11-29-17-51 0Z" fill="#24203c" />
        <path d="M335 509v-62c0-47 19-74 48-74 34 0 55 27 55 74v62H335Z" fill="#7367bd" stroke="#302849" strokeWidth="2.5" />
        <path d="M398 276h197l17 15v240l-17-16H398V276Z" fill="#a996da" stroke="#302849" strokeWidth="3" />
        <path d="M398 276h197v239H398V276Z" fill="url(#signFace)" stroke="#302849" strokeWidth="3" />
        <path d="M595 276l17 15v240l-17-16V276Z" fill="#aa96dc" stroke="#302849" strokeWidth="3" />
        <text x="420" y="308" fill="#574b91" fontSize="12" fontWeight="700" letterSpacing="2" fontFamily="Arial, sans-serif">THE AFTER HOURS</text>
        <text x="420" y="333" fill="#272039" fontSize="17" fontWeight="700" fontFamily="Arial, sans-serif">Find your photos.</text>
        <rect x="418" y="354" width="157" height="142" rx="5" fill="#fff" stroke="#d9d0ef" strokeWidth="1.5" />
        <QRPattern x={437} y={366} size={7.8} />
        <path d="M493 515v41h20v-41M461 556h82" fill="none" stroke="#302849" strokeWidth="3" strokeLinecap="round" />
        <path d="M109 252c12-4 20-13 23-27 3 14 11 23 23 27-12 4-20 13-23 27-3-14-11-23-23-27Z" fill="#d8fa8a" stroke="#302849" strokeWidth="2" />
        <path d="M593 153c8-3 13-9 15-18 2 9 7 15 15 18-8 3-13 9-15 18-2-9-7-15-15-18Z" fill="#d8fa8a" stroke="#302849" strokeWidth="2" />
        <path d="M72 335c18-21 40-30 60-27M602 214c19 8 35 21 45 38" fill="none" stroke="#6d5caf" strokeWidth="2" strokeLinecap="round" strokeDasharray="5 7" />
      </svg>
    </motion.div>;
}
function SelfieVisual() {
  const reduceMotion = useReducedMotion();
  return <div className="selfie-stage" aria-label="Guest selfie shown inside a dimensional phone illustration">
      <div className="selfie-orbit selfie-orbit-one" />
      <div className="selfie-orbit selfie-orbit-two" />
      <svg className="selfie-doodle" viewBox="0 0 130 96" fill="none" aria-hidden="true">
        <path d="M7 72c34 21 86 10 108-37M94 36l23-4-6 23" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m26 19 5-10m9 18 11-7M8 30l-7-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <motion.div
    className="selfie-phone"
    animate={reduceMotion ? { rotate: -8, rotateY: 8 } : { y: [0, -15, 0], rotate: -8, rotateY: 8 }}
    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
  >
        <div className="selfie-phone-screen">
          <img src={images.selfie} alt="Smiling guest taking a selfie at an evening event" loading="lazy" />
          <div className="selfie-phone-shade" />
          <div className="phone-island" />
          <div className="selfie-camera-top"><span>spotfie.</span><span>SELFIE CHECK-IN</span></div>
          <div className="face-frame" aria-hidden="true"><i /><i /><i /><i /></div>
          <div className="selfie-camera-bottom"><span className="camera-dot" />This is your key to the photos.</div>
        </div>
      </motion.div>
      <div className="selfie-spark selfie-spark-one" aria-hidden="true">*</div>
      <div className="selfie-spark selfie-spark-two" aria-hidden="true">*</div>
    </div>;
}
function MatchVisual() {
  const reduceMotion = useReducedMotion();
  return <div className="match-visual" aria-label="Event photographs moving into a personal photo album">
      <svg className="match-path" viewBox="0 0 1200 490" preserveAspectRatio="none" aria-hidden="true">
        <path d="M260 305C395 385 470 76 614 173S840 354 1010 224" fill="none" stroke="#c7bcf1" strokeWidth="2" strokeDasharray="7 10" />
        <path d="m987 215 24 8-18 18" fill="none" stroke="#8170c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="608" cy="170" r="70" fill="none" stroke="#ded6f5" strokeWidth="1.5" />
        <circle cx="608" cy="170" r="103" fill="none" stroke="#e9e4f6" strokeWidth="1.5" />
      </svg>
      <motion.figure className="match-photo match-photo-one" animate={reduceMotion ? { rotate: -9 } : { y: [0, -12, 0], rotate: -9 }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
        <img src={images.friends} alt="Friends laughing together at an event" loading="lazy" />
      </motion.figure>
      <motion.figure className="match-photo match-photo-two" animate={reduceMotion ? { rotate: 9 } : { y: [0, 12, 0], rotate: 9 }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
        <img src={images.candid} alt="A guest caught in a candid moment" loading="lazy" />
      </motion.figure>
      <div className="match-focus" aria-hidden="true">
        <span className="focus-corner focus-tl" /><span className="focus-corner focus-tr" />
        <span className="focus-corner focus-bl" /><span className="focus-corner focus-br" />
        <span className="focus-center" />
      </div>
      <div className="match-album">
        <div className="match-album-sheet match-album-sheet-back" />
        <div className="match-album-sheet match-album-sheet-middle" />
        <div className="match-album-sheet match-album-sheet-front">
          <div className="match-album-heading"><span>THE AFTER HOURS</span><span>PRIVATE ALBUM</span></div>
          <img src={images.friends} alt="Matched photo in a private album" loading="lazy" />
          <p>These moments are yours, Maya.</p>
        </div>
      </div>
    </div>;
}
function AlbumVisual() {
  const reduceMotion = useReducedMotion();
  return <motion.div
    className="album-window-wrap"
    animate={reduceMotion ? void 0 : { y: [0, -10, 0] }}
    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
  >
      <div className="album-window" aria-label="Example of a private guest photo album">
        <div className="album-window-top"><span className="mini-logo">a.</span><span>THE AFTER HOURS</span><span className="album-top-dots"><i /><i /><i /></span></div>
        <div className="album-window-content">
          <p className="album-kicker">YOUR PRIVATE ALBUM</p>
          <h3>These are your<br /><em>moments, Maya.</em></h3>
          <div className="album-grid">
            <img className="album-grid-wide" src={images.friends} alt="Maya laughing with friends" loading="lazy" />
            <img src={images.candid} alt="Maya at the celebration" loading="lazy" />
            <img src={images.hero} alt="Guests arriving at the celebration" loading="lazy" />
          </div>
          <p className="album-private"><LockKeyhole size={14} strokeWidth={1.8} /> Only you can see this album</p>
        </div>
      </div>
    </motion.div>;
}
function useModalControls(onClose) {
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (activeDialogCount === 0) {
      pageOverflowBeforeDialogs = document.body.style.overflow;
      focusBeforeDialogs = document.activeElement;
    }
    activeDialogCount += 1;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll("a[href], button:not([disabled]), input:not([disabled])");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      activeDialogCount -= 1;
      document.removeEventListener("keydown", handleKeyDown);
      if (activeDialogCount === 0) {
        document.body.style.overflow = pageOverflowBeforeDialogs;
        focusBeforeDialogs?.focus();
      }
    };
  }, [onClose]);
  return { closeRef, dialogRef };
}
function GuestDemo({ initialStep, eventName, onClose, onStart }) {
  const [step, setStep] = useState(initialStep);
  const [matching, setMatching] = useState(false);
  const timerRef = useRef(null);
  const { closeRef, dialogRef } = useModalControls(onClose);
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);
  const goForward = () => {
    if (step === 0) setStep(1);
    if (step === 1 && !matching) {
      setMatching(true);
      timerRef.current = window.setTimeout(() => {
        setMatching(false);
        setStep(2);
      }, 950);
    }
  };
  const stages = [
    {
      label: "01 / AT THE ENTRANCE",
      title: "A simple hello.",
      description: "Your guest scans the QR at the event. Their photo journey opens right in the browser, without a download or an account."
    },
    {
      label: "02 / THE SELFIE",
      title: "One quick selfie.",
      description: "A selfie helps find this guest in the event photos. Use our sample selfie to see how the experience continues."
    },
    {
      label: "03 / THE ALBUM",
      title: "Their night, together.",
      description: "The guest lands in a private album of their moments. This is a guided preview with sample photos, not a live face match."
    }
  ];
  return <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose}>
      <motion.div
    ref={dialogRef}
    className="demo-modal modal-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="demo-title"
    initial={{ opacity: 0, y: 24, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 16, scale: 0.98 }}
    transition={{ duration: 0.28 }}
    onMouseDown={(event) => event.stopPropagation()}
  >
        <button className="modal-close" ref={closeRef} type="button" aria-label="Close guest preview" onClick={onClose}><X size={22} /></button>
        <div className="demo-intro">
          <div className="demo-topline"><span>SPOTFIE / GUEST PREVIEW</span><span>0{step + 1} / 03</span></div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.23 }}>
              <p className="modal-eyebrow">{stages[step].label}</p>
              <h2 id="demo-title">{stages[step].title}</h2>
              <p className="demo-description">{stages[step].description}</p>
            </motion.div>
          </AnimatePresence>
          <div className="demo-actions">
            {step > 0 && <button type="button" className="text-action" onClick={() => setStep(step - 1)} disabled={matching}><ChevronLeft size={18} /> Back</button>}
            {step < 2 ? <button className="button button-dark" type="button" onClick={goForward} disabled={matching}>{matching ? "Finding sample moments..." : step === 0 ? "Continue to selfie" : "Use sample selfie"}<ArrowRight size={18} /></button> : <button className="button button-dark" type="button" onClick={onStart} data-testid="guest-demo-live-btn">Explore the live demo<ArrowRight size={18} /></button>}
          </div>
          <div className="demo-progress" aria-label={`Step ${step + 1} of 3`}>
            {[0, 1, 2].map((item) => <span key={item} className={item <= step ? "active" : ""} />)}
          </div>
        </div>
        <div className="demo-visual">
          <div className="demo-device">
            <div className="demo-device-top"><span>9:41</span><span className="demo-device-island" /><span>...</span></div>
            <AnimatePresence mode="wait">
              {step === 0 && <motion.div className="demo-screen" key="entry" initial={{ opacity: 0, x: 25 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -25 }} transition={{ duration: 0.25 }}>
                  <img className="demo-entry-image" src={images.hero} alt="Guests arriving at The After Hours event" />
                  <div className="demo-screen-content">
                    <p className="demo-mini-label">{eventName.toUpperCase()}</p>
                    <h3>So glad<br />you made it.</h3>
                    <p>All the photos you're in, in one little place.</p>
                    <button type="button" onClick={goForward}>Find my photos <ArrowRight size={16} /></button>
                  </div>
                </motion.div>}
              {step === 1 && <motion.div className="demo-screen demo-selfie-screen" key="selfie" initial={{ opacity: 0, x: 25 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -25 }} transition={{ duration: 0.25 }}>
                  <div className="demo-selfie-image"><img src={images.selfie} alt="Sample guest selfie" /><div className="demo-face-frame"><i /><i /><i /><i /></div></div>
                  <div className="demo-selfie-footer"><p className="demo-mini-label">ONE SELFIE. ALL YOUR MOMENTS.</p><h3>Looking good.</h3><button type="button" onClick={goForward} disabled={matching}>{matching ? "Finding moments..." : "Use sample selfie"}<ArrowRight size={16} /></button></div>
                </motion.div>}
              {step === 2 && <motion.div className="demo-screen demo-results-screen" key="results" initial={{ opacity: 0, x: 25 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -25 }} transition={{ duration: 0.25 }}>
                  <div className="demo-result-heading"><p className="demo-mini-label">{eventName.toUpperCase()} / PRIVATE ALBUM</p><h3>Hi, Maya.<br /><em>These are yours.</em></h3></div>
                  <div className="demo-result-images"><img src={images.friends} alt="Friends at the event" /><img src={images.candid} alt="A candid event moment" /></div>
                  <div className="demo-result-footer"><span><LockKeyhole size={13} /> Just for you</span><a href={images.friends} download="spotfie-sample-photo.jpg"><Download size={15} /> Save sample</a></div>
                </motion.div>}
            </AnimatePresence>
            <div className="demo-device-home" />
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
export default function LandingPage() {
  const navigate = useNavigate();
  const { account } = useAuth();
  const [albumPath, setAlbumPath] = useState("/demo");
  useEffect(() => {
    axios.get(`${API}/public/demo`).then((r) => {
      const d = r.data;
      if (d.tenant && d.gallery_id) setAlbumPath(`/t/${d.tenant.slug}/gallery/${d.gallery_id}`);
    }).catch(() => {});
  }, []);
  const openAlbum = () => { setMenuOpen(false); setDemoOpen(false); navigate(albumPath); };
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoEventName, setDemoEventName] = useState("The After Hours");
  const showDemo = (step = 0, eventName = "The After Hours") => {
    setMenuOpen(false);
    setDemoStep(step);
    setDemoEventName(eventName);
    setDemoOpen(true);
  };
  const goDemo = () => { setMenuOpen(false); setDemoOpen(false); navigate("/demo"); };
  return <div id="top" className="site-shell">
      <header className="site-header">
        <div className="header-inner page-container">
          <Logo />
          <nav className="desktop-nav" aria-label="Main navigation">
            {navLinks.map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}
          </nav>
          <div className="header-actions">
            <button type="button" className="header-preview" onClick={openAlbum} data-testid="landing-guest-demo-btn">Open Demo Album <ArrowUpRight size={16} /></button>
            {account ? <Link className="button header-cta" to="/dashboard" data-testid="landing-dashboard-btn">Dashboard <ArrowRight size={17} /></Link> : <><Link className="header-preview" to="/login" data-testid="landing-login-btn"><LogIn size={15} /> Log in</Link><Link className="button header-cta" to="/signup" data-testid="landing-signup-btn">Start free event <ArrowRight size={17} /></Link></>}
          </div>
          <button type="button" className="mobile-menu-button" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X size={25} /> : <Menu size={25} />}</button>
        </div>
        <AnimatePresence>
          {menuOpen && <motion.nav className="mobile-nav" aria-label="Mobile navigation" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
              {navLinks.map((link) => <a key={link.href} href={link.href} onClick={() => setMenuOpen(false)}>{link.label}<ArrowUpRight size={18} /></a>)}
              <button type="button" onClick={openAlbum} data-testid="mobile-guest-album-btn">Open Demo Album <ArrowUpRight size={18} /></button>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Log in <LogIn size={18} /></Link>
              <Link className="mobile-nav-cta" to="/signup" onClick={() => setMenuOpen(false)} data-testid="mobile-signup-btn">Start free event <ArrowRight size={18} /></Link>
            </motion.nav>}
        </AnimatePresence>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <img className="hero-image" src={images.hero} alt="Guests arriving and celebrating at an evening event" fetchPriority="high" />
          <div className="hero-overlay" />
          <div className="hero-content page-container">
            <motion.h1 id="hero-title" initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}>spotfie<span>.</span></motion.h1>
            <motion.div className="hero-bottom" initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}>
              <div className="hero-message">
                <h2>The photos find<br />their people.</h2>
                <p>One selfie per guest turns your event shoot into private albums, automatically. No searching, no tagging, no app.</p>
                <div className="hero-buttons">
                  <Link className="button button-lime" to="/signup" data-testid="hero-signup-btn">Start your free event <ArrowRight size={18} /></Link>
                  <a className="button button-dark" href="#journey" data-testid="hero-how-btn">See how it works <ArrowDown size={18} /></a>
                  <button className="hero-text-button" type="button" onClick={openAlbum} data-testid="hero-demo-btn">Open Demo Album <ArrowUpRight size={17} /></button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="journey-intro" id="journey">
          <div className="page-container journey-intro-inner">
            <Reveal><SectionLabel number="THE JOURNEY">FROM HELLO TO HERE IT IS</SectionLabel></Reveal>
            <Reveal delay={0.08}><h2>From the first hello<br />to the <em>last photo.</em></h2></Reveal>
            <Reveal delay={0.15}><p>Getting the photos should feel as good as being there. Here is the simple journey from arrival to album.</p></Reveal>
          </div>
        </section>

        <section className="story-section entrance-section" aria-labelledby="entrance-title">
          <div className="page-container story-grid entrance-grid">
            <Reveal className="story-copy">
              <SectionLabel number="01">THE ENTRANCE</SectionLabel>
              <h2 id="entrance-title">A good night starts with <em>an easy hello.</em></h2>
              <p>Put your event QR where people arrive. Guests scan it and step straight into their photo experience, right in their browser.</p>
              <button type="button" className="inline-link" onClick={() => showDemo(0)}>See the guest entrance <ArrowUpRight size={19} /></button>
            </Reveal>
            <Reveal className="story-art entrance-art-wrap" delay={0.14}><EntranceIllustration /></Reveal>
          </div>
        </section>

        <section className="story-section selfie-section" aria-labelledby="selfie-title">
          <div className="page-container story-grid selfie-grid">
            <Reveal className="story-art selfie-art-wrap"><SelfieVisual /></Reveal>
            <Reveal className="story-copy" delay={0.12}>
              <SectionLabel number="02">THE SELFIE</SectionLabel>
              <h2 id="selfie-title">One selfie.<br /><em>That's the key.</em></h2>
              <p>A guest takes a quick selfie to find themselves in the photos. It is used for matching, then discarded. Their night stays theirs.</p>
              <button type="button" className="inline-link" onClick={() => showDemo(1)}>Try the selfie step <ArrowUpRight size={19} /></button>
            </Reveal>
          </div>
        </section>

        <section className="match-section" aria-labelledby="match-title">
          <div className="page-container">
            <div className="match-heading">
              <Reveal>
                <SectionLabel number="03">BEHIND THE SCENES</SectionLabel>
                <h2 id="match-title">Every camera goes in.<br /><em>The right photos come out.</em></h2>
              </Reveal>
              <Reveal delay={0.1}><p>Your whole team uploads to one event. Spotfie finds registered guests across the shoot and organizes their moments automatically.</p></Reveal>
            </div>
            <Reveal className="match-art-wrap" delay={0.1}><MatchVisual /></Reveal>
          </div>
        </section>

        <section className="story-section delivery-section" aria-labelledby="delivery-title">
          <div className="page-container story-grid delivery-grid">
            <Reveal className="story-copy">
              <SectionLabel number="04">THE REVEAL</SectionLabel>
              <h2 id="delivery-title">Not a folder.<br /><em>Their own little world.</em></h2>
              <p>Every guest opens a private, event-branded album of the moments they are actually in. Ready to view, save, and share while the feeling is still fresh.</p>
              <button type="button" className="inline-link" onClick={openAlbum} data-testid="journey-open-album-btn">Open Demo Album <ArrowUpRight size={19} /></button>
            </Reveal>
            <Reveal className="story-art delivery-art-wrap" delay={0.1}><AlbumVisual /></Reveal>
          </div>
        </section>

        <section id="for-teams" className="benefits-section" aria-labelledby="benefits-title">
          <div className="page-container benefits-inner">
            <Reveal className="benefits-heading">
              <SectionLabel number="WHY SPOTFIE">BETTER ON BOTH SIDES OF THE CAMERA</SectionLabel>
              <h2 id="benefits-title">More afterglow.<br /><em>Less admin.</em></h2>
              <p>Made to feel effortless for guests and genuinely useful for the people making the event happen.</p>
            </Reveal>
            <div className="benefits-list">
              {[
    { number: "01", title: "One home for the whole shoot.", copy: "Every photographer adds to the same event. Nobody has to sort a mountain of files by hand." },
    { number: "02", title: "Private feels personal.", copy: "Guests see their own matched moments, not a public folder of everyone else's photos." },
    { number: "03", title: "Your name gets the spotlight.", copy: "Make the guest page feel like your event. Go fully white-label with the Pro Event Pass." }
  ].map((item, index) => <Reveal key={item.number} delay={index * 0.06}>
                  <div className="benefit-row"><span>{item.number}</span><h3>{item.title}</h3><p>{item.copy}</p><ArrowUpRight size={21} strokeWidth={1.6} aria-hidden="true" /></div>
                </Reveal>)}
            </div>
          </div>
        </section>

        <section id="pricing" className="pricing-section" aria-labelledby="pricing-title">
          <div className="page-container">
            <Reveal className="pricing-heading">
              <SectionLabel number="PRICING">SIMPLE BY DESIGN</SectionLabel>
              <h2 id="pricing-title">Start free. Make it <em>yours.</em></h2>
              <p>Try Spotfie with your first event. When you need the fully white-label version, pay once for that event. No monthly subscription.</p>
            </Reveal>
            <div className="pricing-table">
              <Reveal className="price-column">
                <div className="price-topline"><span>YOUR FIRST EVENT</span><span>01</span></div>
                <h3>Free</h3>
                <p className="price-amount"><span>{"\u20B9"}</span>0 <small>/ first event</small></p>
                <p className="price-description">The whole experience, ready for your first shoot.</p>
                <ul>
                  <li><Check size={17} />Unlimited photographers in one folder</li>
                  <li><Check size={17} />Private photo album for each guest</li>
                  <li><Check size={17} />Event QR and guest portal</li>
                  <li><Check size={17} />Spotfie footer included</li>
                </ul>
                <Link className="button price-button price-button-outline" to="/signup" data-testid="pricing-free-cta">Start your free event <ArrowRight size={18} /></Link>
              </Reveal>
              <Reveal className="price-column price-column-pro" delay={0.1}>
                <div className="price-topline"><span>PRO EVENT PASS</span><span>02</span></div>
                <h3>All yours.</h3>
                <p className="price-amount"><span>{"\u20B9"}</span>4,999 <small>/ event, one-time</small></p>
                <p className="price-description">For the moments that should have only your name on them.</p>
                <ul>
                  <li><Check size={17} />Everything in the Free event</li>
                  <li><Check size={17} />Your branding, no Spotfie footer</li>
                  <li><Check size={17} />Unlimited guest registrations</li>
                  <li><Check size={17} />Priority matching queue</li>
                </ul>
                <Link className="button price-button price-button-solid" to="/signup" data-testid="pricing-pro-cta">Get the Pro Event Pass <ArrowRight size={18} /></Link>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="faq" className="faq-section" aria-labelledby="faq-title">
          <div className="page-container faq-grid">
            <Reveal className="faq-heading"><SectionLabel number="FAQ">A FEW GOOD QUESTIONS</SectionLabel><h2 id="faq-title">The details,<br /><em>made simple.</em></h2></Reveal>
            <div className="faq-list">
              {faqItems.map((item, index) => <Reveal key={item.question} delay={index * 0.04}>
                  <div className={`faq-item ${openFaq === index ? "faq-item-open" : ""}`}>
                    <h3><button type="button" id={`faq-question-${index}`} aria-controls={`faq-answer-${index}`} aria-expanded={openFaq === index} onClick={() => setOpenFaq(openFaq === index ? null : index)}><span>{item.question}</span><span className="faq-toggle">{openFaq === index ? <Minus size={20} /> : <Plus size={20} />}</span></button></h3>
                    <AnimatePresence initial={false}>
                      {openFaq === index && <motion.div id={`faq-answer-${index}`} className="faq-answer" role="region" aria-labelledby={`faq-question-${index}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}><p>{item.answer}</p></motion.div>}
                    </AnimatePresence>
                  </div>
                </Reveal>)}
            </div>
          </div>
        </section>

        <section className="final-section" aria-labelledby="final-title">
          <div className="page-container final-inner">
            <Reveal><SectionLabel number="YOUR NEXT EVENT">A BETTER LAST MILE</SectionLabel><h2 id="final-title">Let the night happen.<br /><em>We'll find the photos.</em></h2></Reveal>
            <Reveal className="final-action" delay={0.1}><p>Give every guest a way back to their favorite moments.</p><Link className="button button-dark final-button" to="/signup" data-testid="final-cta-signup">Create your free event <ArrowUpRight size={21} /></Link></Reveal>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="page-container footer-inner">
          <div><Logo /><p>Every guest has a story in the photos.</p></div>
          <nav aria-label="Footer navigation">{navLinks.map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}</nav>
          <div className="footer-end"><button type="button" onClick={openAlbum} data-testid="footer-guest-album">Open Demo Album <ArrowUpRight size={15} /></button><Link to="/demo" data-testid="footer-live-portal">Explore the live demo <ArrowUpRight size={15} /></Link><Link to="/login" data-testid="footer-login">Client login <ArrowUpRight size={15} /></Link><span>&copy; {new Date().getFullYear()} Spotfie</span></div>
        </div>
      </footer>

      <AnimatePresence>
        {demoOpen && <GuestDemo key={`demo-${demoStep}`} initialStep={demoStep} eventName={demoEventName} onClose={() => setDemoOpen(false)} onStart={goDemo} />}
      </AnimatePresence>
    </div>;
}
